<script setup lang="ts">
import {computed, ref} from "vue";
import {useIntersectionObserver} from "@vueuse/core";
import {Circle, CircleCheck, FileX, Maximize2, Sparkles} from "@lucide/vue";
import MedicationCheckupPosologyLine from "@/medication-checkup/components/MedicationCheckupPosologyLine.vue";
import MedicationCheckupPrescriptionPage from "@/medication-checkup/components/MedicationCheckupPrescriptionPage.vue";
import MedicationCheckupDispensingsTag from "@/medication-checkup/components/MedicationCheckupDispensingsTag.vue";
import {TreatmentLine} from "@/medication-checkup/interfaces/treatment-selections";
import {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";
import {useTreatmentSelectionsStore} from "@/medication-checkup/stores/treatment-selections-store";
import {pluralize} from "@/core/filters/Pluralize";

/**
 * A prescription of the collection, drawn as the sheet it is: its first page
 * in large, the lines delivered on it listed beside the page, to check and
 * dose with the whole document in sight.
 *
 * The page is fetched once the sheet scrolls into the strip, never before —
 * the HDS rations the pharmacy's requests. A sheet standing for a prescriber
 * whose prescription was not scanned has no page to show.
 */

const props = defineProps<{
    sheet: TreatmentSheet;
}>();

const emit = defineEmits<{
    /**
     * The sheet opened full screen.
     */
    open: [];
    edit: [line: TreatmentLine];
}>();

const selection = useTreatmentSelectionsStore();

const root = ref<HTMLElement | null>(null);
const seen = ref(false);

const {stop} = useIntersectionObserver(root, ([entry]) => {
    if (entry?.isIntersecting) {
        seen.value = true;
        stop();
    }
});

const retainedCount = computed(() => props.sheet.lines.filter(selection.isChecked).length);
</script>

<template>
  <div ref="root" class="flex shrink-0 flex-col gap-1">
    <div class="flex items-stretch gap-3">
      <div
        class="h-[678px] w-[480px] overflow-hidden rounded-[3px] bg-white shadow-[0_3px_14px_rgba(19,45,90,.16)]"
        :class="{'cursor-zoom-in': sheet.prescriptionKey}"
        @click="sheet.prescriptionKey && emit('open')"
      >
        <MedicationCheckupPrescriptionPage
          v-if="sheet.prescriptionKey"
          :prescription-key="sheet.prescriptionKey"
          :wanted="seen"
          pager="corners"
        >
          <template #corner>
            <span
              class="flex size-[26px] items-center justify-center rounded-full bg-white/90 text-primary shadow-sm"
              aria-hidden="true"
            >
              <Maximize2 class="size-3.5" />
            </span>
          </template>
        </MedicationCheckupPrescriptionPage>

        <div v-else class="flex h-full flex-col items-center justify-start gap-2 bg-gray-light pt-28 text-center text-muted-foreground">
          <FileX class="size-10 stroke-1" />
          <span class="text-sm font-semibold">Ordonnance non numérisée</span>
        </div>
      </div>

      <div class="flex h-[678px] w-[320px] shrink-0 flex-col overflow-hidden rounded-lg border border-solid border-slate-200 bg-white">
        <div class="flex flex-col gap-0.5 border-b border-solid border-gray-light px-3.5 pb-2.5 pt-3">
          <span class="text-[13px] font-bold text-gray-dark">{{ sheet.prescriber ?? 'Prescripteur inconnu' }}</span>
          <span v-if="sheet.deliveredAt" class="text-[11px] text-muted-foreground">
            Délivrée le {{ $filters.date(sheet.deliveredAt, 'dayOnly') }}
          </span>
          <span class="mt-1 flex items-center gap-1.5">
            <Sparkles class="size-3.5 text-primary" />
            <span class="text-[11px] font-bold text-primary">
              {{ sheet.lines.length }} {{ pluralize('délivré', sheet.lines.length) }}
            </span>
            <span class="text-[11px] font-semibold text-secondary-darkest">
              · {{ retainedCount }} {{ pluralize('retenu', retainedCount) }}
            </span>
          </span>
        </div>

        <div class="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto p-2">
          <div
            v-for="line in sheet.lines"
            :key="line.id"
            class="flex flex-col gap-1.5 rounded-[9px] border border-solid px-2.5 py-2.5 transition-colors"
            :class="selection.isChecked(line) ? 'border-secondary-darkest/45 bg-secondary-lightest/30' : 'border-gray-light bg-white'"
          >
            <span class="flex items-start gap-2.5">
              <button
                type="button"
                class="mt-px shrink-0"
                :title="selection.isChecked(line) ? 'Retirer du bilan' : 'Retenir pour le bilan'"
                @click="selection.toggle(line)"
              >
                <CircleCheck v-if="selection.isChecked(line)" class="size-[18px] text-secondary-darkest" />
                <Circle v-else class="size-[18px] text-gray" />
              </button>
              <button
                type="button"
                class="text-left text-[12.5px] font-semibold text-gray-dark"
                @click="selection.toggle(line)"
              >
                {{ line.label }}
              </button>
            </span>
            <MedicationCheckupPosologyLine
              :posology="selection.posology(line)"
              editable
              :asked="selection.isChecked(line)"
              @edit="emit('edit', line)"
            />
            <MedicationCheckupDispensingsTag v-if="line.history" :history="line.history" />
          </div>
        </div>
      </div>
    </div>

    <span class="w-[480px] truncate text-right text-[11px] font-semibold text-gray-dark">
      {{ sheet.prescriber ?? 'Prescripteur inconnu' }}<template v-if="sheet.deliveredAt"> · délivrée le {{ $filters.date(sheet.deliveredAt, 'dayOnly') }}</template>
    </span>
  </div>
</template>
